<template>
  <div class="container">
    <div class="box-title">
      <h4>
        <i class="fal fa-map-marked-alt"></i>
        {{ editingRouteId ? "ແກ້ໄຂເສັ້ນທາງ" : "ສ້າງເສັ້ນທາງໃໝ່" }}
        <span
          v-if="routeDetail"
          :style="{ fontSize: '14px', marginLeft: '10px' }"
        >
          <span
            :style="{ color: routeDetail.is_active ? '#2dcc70' : '#e8a10f' }"
          >
            ({{ routeDetail.is_active ? "ເປີດໃຊ້ງານ" : "ສະບັບຮ່າງ" }})
          </span>
        </span>
        <span class="btn-add">
          <vs-button flat @click="BackToList()">
            <i class="fal fa-arrow-left" :style="{ marginRight: '5px' }"></i>
            ກັບຄືນ
          </vs-button>
        </span>
      </h4>
      <!-- Full route name up top: province route names ARE the stop chain,
           so the admin reads each part here while managing the stops below -->
      <p v-if="routeValue.name" class="route-name-top">
        <i class="fal fa-route"></i>
        {{ routeValue.name }}
      </p>
    </div>

    <div class="columns">
      <!-- Map -->
      <div class="column is-7">
        <div class="map-wrap">
          <GmapMap
            :center="mapCenter"
            :zoom="8"
            :options="mapOptions"
            :map-type-id="mapTypeId"
            style="width: 100%; height: 620px"
            @click="OnMapClick"
          >
            <GmapMarker
              v-if="startStop && Number(startStop.lat)"
              :position="{
                lat: Number(startStop.lat),
                lng: Number(startStop.lng),
              }"
              :icon="startIcon"
              :label="{ text: 'S', color: 'white', fontWeight: 'bold' }"
              :draggable="!!startStop.coordsDirty"
              @dragend="OnStartDragEnd"
            />
            <GmapMarker
              v-for="(stop, index) in mappableStops"
              :key="'stop-' + index"
              :position="{ lat: Number(stop.lat), lng: Number(stop.lng) }"
              :icon="stop.type === 'distribution_center' ? dcIcon : branchIcon"
              :label="{
                text: String(stop.displaySequence),
                color: 'white',
                fontWeight: 'bold',
              }"
              :draggable="!!stop.coordsDirty"
              @dragend="OnStopDragEnd(stop.origIndex, $event)"
              @click="
                mapCenter = { lat: Number(stop.lat), lng: Number(stop.lng) }
              "
            />
            <GmapPolyline
              v-if="displayPolylinePath.length > 1"
              :path="displayPolylinePath"
              :options="{
                strokeColor: '#195BFF',
                strokeOpacity: 0.85,
                strokeWeight: 4,
              }"
            />
          </GmapMap>
          <button
            class="map-mode-btn"
            :title="'ປ່ຽນມຸມມອງແຜນທີ່'"
            @click="CycleMapMode"
          >
            <i :class="currentMapMode.icon"></i> {{ currentMapMode.label }}
          </button>
          <div class="map-placement-banner" v-if="placementTarget">
            <i class="fal fa-crosshairs"></i>
            ກົດໃສ່ແຜນທີ່ເພື່ອວາງໝຸດ: <strong>{{ placementName }}</strong>
            <a @click="placementTarget = null">ຍົກເລີກ</a>
          </div>
        </div>
        <!-- Coordinate editor: type/paste coords OR click the map / drag the pin — all stay in sync -->
        <div class="box coord-editor" v-if="placementTarget">
          <label class="label">
            <i
              class="fal fa-map-pin"
              :style="{ marginRight: '5px', color: '#e8a10f' }"
            ></i>
            ພິກັດ: {{ placementName }}
          </label>
          <div class="columns" :style="{ marginBottom: '0' }">
            <!-- ONE box, exactly what Google Maps hands you: right-click the
                 place, click the coordinates to copy, paste "lat, lng" here.
                 The parser also tolerates parentheses and a full Maps URL. -->
            <div class="column is-8" :style="{ paddingBottom: '0' }">
              <input
                class="input"
                placeholder="ວາງພິກັດຈາກ Google Maps ເຊັ່ນ: 19.8845, 102.1350"
                v-model="coordText"
                @input="ApplyCoordInput"
                @change="CenterOnTarget"
              />
            </div>
            <div
              class="column is-4 coord-actions"
              :style="{ paddingBottom: '0' }"
            >
              <vs-button
                size="small"
                flat
                success
                :loading="coordSaving"
                @click="SaveCoordEditor()"
              >
                <i class="fal fa-save" :style="{ marginRight: '5px' }"></i>
                ບັນທຶກພິກັດ
              </vs-button>
              <vs-button size="small" flat @click="placementTarget = null"
                >ປິດ</vs-button
              >
            </div>
          </div>
          <p :style="{ fontSize: '12px', color: 'grey', marginTop: '5px' }">
            ວາງ "lat, lng" ທີ່ກັອບປີ້ຈາກ Google Maps ໃສ່ຊ່ອງດຽວໄດ້ເລີຍ
            (ຮັບທັງລິ້ງ Google Maps) ຫຼື ກົດໃສ່ແຜນທີ່ / ລາກໝຸດ
          </p>
        </div>
        <p :style="{ fontSize: '12px', color: 'grey', marginTop: '5px' }">
          <i class="fas fa-circle" :style="{ color: '#2dcc70' }"></i>
          ຈຸດເລີ່ມຕົ້ນ
          <i
            class="fas fa-circle"
            :style="{ color: '#195BFF', marginLeft: '10px' }"
          ></i>
          ສາຂາ
          <i
            class="fas fa-circle"
            :style="{ color: '#e8a10f', marginLeft: '10px' }"
          ></i>
          ສູນຄັດແຍກ — ເສັ້ນເຊື່ອມຕາມລຳດັບຈຸດແວະ. ກົດ
          <i class="fal fa-crosshairs"></i> ເພື່ອວາງໝຸດ/ພິມພິກັດ, ແລ້ວກົດ
          <i class="fal fa-save"></i> ບັນທຶກ
        </p>
      </div>

      <!-- Builder panel -->
      <div class="column is-5">
        <!-- Stop search -->
        <div class="box">
          <label class="label">ຄົ້ນຫາຈຸດແວະ (ສາຂາ / ສູນຄັດແຍກ)</label>
          <div class="columns" :style="{ marginBottom: '0' }">
            <div class="column is-5" :style="{ paddingBottom: '0' }">
              <input
                class="input"
                placeholder="ຊື່ ຫຼື ລະຫັດ..."
                v-model="stopSearchTerm"
                @keyup.enter="SearchStopPoints"
              />
            </div>
            <div class="column is-4" :style="{ paddingBottom: '0' }">
              <div class="select" :style="{ width: '100%' }">
                <select
                  class="input"
                  v-model="stopSearchProvince"
                  @change="SearchStopPoints"
                >
                  <option value="">ທຸກແຂວງ</option>
                  <option
                    v-for="(province, index) in provinces"
                    :key="index"
                    :value="province.id"
                  >
                    {{ province.name }}
                  </option>
                </select>
              </div>
            </div>
            <div class="column is-3" :style="{ paddingBottom: '0' }">
              <div class="select" :style="{ width: '100%' }">
                <select
                  class="input"
                  v-model="stopSearchType"
                  @change="SearchStopPoints"
                >
                  <option value="">ທັງສອງ</option>
                  <option value="branch">ສາຂາ</option>
                  <option value="distribution_center">ສູນ</option>
                </select>
              </div>
            </div>
          </div>
          <vs-button
            flat
            size="small"
            :loading="stopSearchLoading"
            @click="SearchStopPoints"
          >
            <i class="fal fa-search" :style="{ marginRight: '5px' }"></i> ຄົ້ນຫາ
          </vs-button>
          <div class="stop-search-results" v-if="stopSearchResults.length">
            <div
              class="stop-search-row"
              v-for="(point, index) in stopSearchResults"
              :key="index"
            >
              <span>
                <i
                  :class="
                    point.type === 'distribution_center'
                      ? 'fal fa-warehouse'
                      : 'fal fa-store'
                  "
                  :style="{
                    color:
                      point.type === 'distribution_center'
                        ? '#e8a10f'
                        : '#195BFF',
                    marginRight: '5px',
                  }"
                ></i>
                {{ point.name }}
                <span :style="{ color: 'grey' }">({{ point.prefix }})</span>
              </span>
              <span>
                <vs-button size="small" flat @click="SetStartStop(point)"
                  >ຈຸດເລີ່ມ</vs-button
                >
                <vs-button size="small" flat success @click="AddStop(point)"
                  >+ ຈຸດແວະ</vs-button
                >
              </span>
            </div>
          </div>
        </div>

        <!-- Ordered stop list -->
        <div class="box">
          <label class="label">
            ຈຸດເລີ່ມຕົ້ນ
            <span class="text-danger">{{
              server_errors["start_stop.id"] || server_errors.start_stop
            }}</span>
          </label>
          <div class="stop-row start" v-if="startStop">
            <span class="stop-name">
              <i
                class="fal fa-flag-checkered"
                :style="{ marginRight: '5px', color: '#2dcc70' }"
              ></i>
              {{ startStop.name }}
              <span :style="{ color: 'grey' }"
                >({{ FleetStopTypeLabel(startStop.type) }})</span
              >
            </span>
            <span class="stop-actions">
              <vs-button
                circle
                icon
                flat
                :warn="!Number(startStop.lat)"
                size="small"
                title="ວາງໝຸດໃສ່ແຜນທີ່ ຫຼື ພິມພິກັດເອງ"
                @click="ArmPlacement('start')"
              >
                <i class="fal fa-crosshairs"></i>
              </vs-button>
              <vs-button
                v-if="startStop.coordsDirty"
                circle
                icon
                flat
                success
                size="small"
                title="ບັນທຶກພິກັດລົງຖານຂໍ້ມູນ"
                @click="SaveStartLocation()"
              >
                <i class="fal fa-save"></i>
              </vs-button>
              <vs-button
                circle
                icon
                flat
                danger
                size="small"
                title="ປ່ຽນ / ລຶບຈຸດເລີ່ມ — ຈາກນັ້ນຄົ້ນຫາ ແລ້ວກົດ ຈຸດເລີ່ມ ໃໝ່"
                @click="ClearStartStop()"
              >
                <i class="fal fa-times"></i>
              </vs-button>
            </span>
          </div>
          <p v-else :style="{ color: 'grey', fontSize: '13px' }">
            ຍັງບໍ່ໄດ້ເລືອກຈຸດເລີ່ມຕົ້ນ — ຄົ້ນຫາ ແລ້ວກົດ "ຈຸດເລີ່ມ"
          </p>
          <p
            v-if="startStop"
            :style="{ color: '#2dcc70', fontSize: '12px', marginTop: '3px' }"
          >
            <i class="fal fa-truck-loading" :style="{ marginRight: '4px' }"></i>
            ຕົ້ນທາງ — outbound ພັດສະດຸຂຶ້ນລົດຈາກຈຸດນີ້ ໄປສົ່ງແຕ່ລະຈຸດແວະ
            (ບໍ່ແມ່ນຈຸດຮັບ/ລໍຖ້າ)
          </p>

          <label class="label" :style="{ marginTop: '15px' }">
            ຈຸດແວະຕາມລຳດັບ ({{ routeStops.length }})
            <span class="text-danger">{{ server_errors.stops }}</span>
          </label>
          <div
            class="stop-row"
            v-for="(stop, index) in routeStops"
            :key="index"
          >
            <span class="stop-seq">{{ index + 1 }}</span>
            <span class="stop-name">
              {{ stop.name }}
              <span :style="{ color: 'grey', fontSize: '12px' }"
                >({{ FleetStopTypeLabel(stop.type) }})</span
              >
            </span>
            <span class="stop-offset">
              <input
                class="input"
                type="number"
                min="0"
                placeholder="ນາທີ"
                v-model="stop.planned_offset_min"
                title="ເວລາຈາກຈຸດເລີ່ມ (ນາທີ)"
              />
            </span>
            <span class="stop-actions">
              <vs-button
                circle
                icon
                flat
                :warn="!Number(stop.lat)"
                size="small"
                title="ວາງໝຸດໃສ່ແຜນທີ່ ຫຼື ພິມພິກັດເອງ"
                @click="ArmPlacement(index)"
              >
                <i class="fal fa-crosshairs"></i>
              </vs-button>
              <vs-button
                v-if="stop.coordsDirty"
                circle
                icon
                flat
                success
                size="small"
                title="ບັນທຶກພິກັດລົງຖານຂໍ້ມູນ"
                @click="SaveStopLocation(index)"
              >
                <i class="fal fa-save"></i>
              </vs-button>
              <vs-button
                circle
                icon
                flat
                size="small"
                :disabled="index === 0"
                @click="MoveStopUp(index)"
              >
                <i class="fal fa-arrow-up"></i>
              </vs-button>
              <vs-button
                circle
                icon
                flat
                size="small"
                :disabled="index === routeStops.length - 1"
                @click="MoveStopDown(index)"
              >
                <i class="fal fa-arrow-down"></i>
              </vs-button>
              <vs-button
                circle
                icon
                flat
                danger
                size="small"
                @click="RemoveStop(index)"
              >
                <i class="fal fa-times"></i>
              </vs-button>
            </span>
          </div>
        </div>

        <!-- Meta form -->
        <div class="box">
          <div class="columns is-multiline">
            <div class="column is-7">
              <label class="label">
                ຊື່ເສັ້ນທາງ
                <span class="text-danger">* {{ errors.first("name") }}</span>
                <span class="text-danger">{{ server_errors.name }}</span>
              </label>
              <input
                class="input"
                name="name"
                placeholder="ຊື່ເສັ້ນທາງ..."
                v-model="routeValue.name"
                v-validate="'required'"
              />
            </div>
            <div class="column is-5">
              <label class="label"
                >ລະຫັດ
                <span class="text-danger">{{ server_errors.code }}</span></label
              >
              <input
                class="input"
                placeholder="ML1..."
                v-model="routeValue.code"
              />
            </div>
            <div class="column is-12">
              <label class="label">ເສັ້ນທາງທີ່ແລ່ນ (ຄຳອະທິບາຍ)</label>
              <input
                class="input"
                placeholder="ຕົວຢ່າງ: ວຽງຈັນ - ຫຼວງພະບາງ..."
                v-model="routeValue.running_route"
              />
            </div>
            <div class="column is-4">
              <label class="label">ໄລຍະທາງ (km)</label>
              <input
                class="input"
                type="number"
                min="0"
                v-model="routeValue.distance_km"
              />
            </div>
            <!-- Transport MONEY (fleet-cost permission). A planner without it
                 never sees these; the API also refuses to read them back OR to
                 write them, so a planner's Save cannot wipe what finance set. -->
            <div class="column is-4" v-if="CanViewCost">
              <label class="label">ລາຄາ/ຖ້ຽວ</label>
              <input
                class="input"
                type="number"
                min="0"
                v-model="routeValue.standard_trip_price"
              />
            </div>
            <div class="column is-4">
              <label class="label">ຖ້ຽວ/ອາທິດ</label>
              <input
                class="input"
                type="number"
                min="0"
                v-model="routeValue.trips_per_week"
              />
            </div>
            <!-- Litres are a physical standard, not money — planners keep them. -->
            <div :class="CanViewCost ? 'column is-6' : 'column is-12'">
              <label class="label">ນ້ຳມັນມາດຕະຖານ (ລິດ)</label>
              <input
                class="input"
                type="number"
                min="0"
                v-model="routeValue.standard_fuel_liters"
              />
            </div>
            <div class="column is-6" v-if="CanViewCost">
              <label class="label">ຄ່ານ້ຳມັນມາດຕະຖານ</label>
              <input
                class="input"
                type="number"
                min="0"
                v-model="routeValue.standard_fuel_amount"
              />
            </div>
            <!-- Daily auto-created rounds: at 18:00 the system copies this
                 route's most recent day of rounds onto tomorrow (approved), so
                 the route QR always finds a trip. -->
            <div class="column is-12">
              <label class="checkbox auto-daily-toggle">
                <input type="checkbox" v-model="routeValue.auto_daily_rounds" />
                ສ້າງຖ້ຽວອັດຕະໂນມັດທຸກມື້ (18:00 ສ້າງຖ້ຽວຂອງມື້ອື່ນ ຈາກຖ້ຽວມື້ລ່າສຸດ)
              </label>
            </div>
          </div>

          <!-- Geofence advisory: two consecutive stops so close that the
               earlier one can never clear its exit radius, so auto check-in
               stalls there. Advisory only — the route still saves. -->
          <div v-if="GeofenceWarnings.length" class="geofence-warn">
            <p class="geofence-warn-title">
              <i class="fal fa-exclamation-triangle"></i>
              ຈຸດໝາຍແວະຢູ່ໃກ້ກັນເກີນໄປ (ລະບົບຈະບໍ່ສາມາດເຊັກອິນອັດຕະໂນມັດໄດ້)
            </p>
            <p
              v-for="(w, i) in GeofenceWarnings"
              :key="i"
              class="geofence-warn-line"
            >
              <b>{{ w.from.name || "#" + w.from.stop_id }}</b> ↔
              <b>{{ w.to.name || "#" + w.to.stop_id }}</b>
              ຫ່າງກັນພຽງ {{ w.meters }} ແມັດ (ລົດຕ້ອງຂັບອອກຫ່າງ
              {{ w.exit_radius_m }} ແມັດ ລະບົບຈຶ່ງຈະບັນທຶກວ່າອອກຈາກຈຸດ)
            </p>
            <p class="geofence-warn-note">
              ເນື່ອງຈາກສອງຈຸດນີ້ຢູ່ໃກ້ກັນຫຼາຍ, ເມື່ອລົດຈອດຢູ່ຈຸດທຳອິດ ລະບົບຈະຖືວ່າລົດຢູ່ໃນລັດສະໝີຂອງຈຸດທີສອງນຳ. ເຮັດໃຫ້ລະບົບ GPS ບໍ່ສາມາດອັບເດດສະຖານະ "ອອກຈາກຈຸດທີ່ 1" ແລະ "ຮອດຈຸດທີ່ 2" ແບບອັດຕະໂນມັດໃຫ້ໄດ້.<br><br>
              <b>ຄຳແນະນຳ:</b> ທ່ານສາມາດລວມສອງຈຸດນີ້ເຂົ້າກັນ, ປ່ຽນລຳດັບການເດີນທາງ, ຫຼື ຖ້າຮັກສາໄວ້ຄືເກົ່າ ຄົນຂັບຈະຕ້ອງໄດ້ກົດເຊັກອິນເຂົ້າຈຸດທີສອງດ້ວຍຕົນເອງ.
            </p>
          </div>

          <div class="center" :style="{ marginTop: '10px' }">
            <div class="con-btns">
              <vs-button
                flat
                class="form-btn-loading"
                :loading="btnLoading"
                @click="ValidateAndSave()"
              >
                {{
                  editingRouteId
                    ? "ບັນທຶກການແກ້ໄຂ"
                    : "ບັນທຶກເສັ້ນທາງ (ສະບັບຮ່າງ)"
                }}
              </vs-button>
              <vs-button
                v-if="editingRouteId && routeDetail"
                flat
                :success="!routeDetail.is_active"
                :danger="routeDetail.is_active"
                @click="ToggleActive()"
              >
                {{
                  routeDetail.is_active
                    ? "ປິດເປັນສະບັບຮ່າງ"
                    : "ເປີດໃຊ້ງານເສັ້ນທາງ"
                }}
              </vs-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MixinFleetRoutes from "@/Mixins/Fleet/Routes";
import {
  fleetPinIcon,
  fleetRoadPath,
  FLEET_MAP_MODES,
} from "@/Mixins/Fleet/FleetHelpers";

// DB "missing" sentinel is 0/0 (every DC ships that way) — treat it as null.
const coord = (value) => (Number(value) ? Number(value) : null);

export default {
  mixins: [MixinFleetRoutes],
  data: () => ({
    editingRouteId: null,
    mapCenter: { lat: 18.1189434, lng: 102.290218 },
    // One cycle button replaces Google's own map-type bar (mapTypeControl off).
    mapTypeId: "roadmap",
    mapModes: FLEET_MAP_MODES,
    // Free-pin placement for facilities without coordinates (all DCs).
    placementTarget: null, // {kind: 'start'} | {kind: 'stop', index}
    coordText: "", // single "lat, lng" box — manual-entry mirror of the armed target
    coordSaving: false,
    mapOptions: {
      zoomControl: true,
      scaleControl: true,
      fullscreenControl: true,
      streetViewControl: false,
      mapTypeControl: false,
    },
    startIcon: fleetPinIcon("#2dcc70"),
    branchIcon: fleetPinIcon("#195BFF"),
    dcIcon: fleetPinIcon("#e8a10f"),
    roadPath: null, // Directions-service road geometry (null = straight fallback)
    roadPathKey: "",
    roadPathTimer: null,
  }),
  computed: {
    // Server-computed: consecutive stops too close for auto check-in to
    // advance between them. Absent on an older backend, so default to [].
    GeofenceWarnings() {
      return (this.routeDetail && this.routeDetail.geofence_warnings) || [];
    },
    currentMapMode() {
      return (
        this.mapModes.find((mode) => mode.id === this.mapTypeId) ||
        this.mapModes[0]
      );
    },
    placementName() {
      if (!this.placementTarget) return "";
      if (this.placementTarget.kind === "start")
        return this.startStop ? this.startStop.name : "";
      const stop = this.routeStops[this.placementTarget.index];
      return stop ? stop.name : "";
    },
    mappableStops() {
      return this.routeStops
        .map((stop, index) => ({
          ...stop,
          displaySequence: index + 1,
          origIndex: index,
        }))
        .filter((stop) => Number(stop.lat) && Number(stop.lng));
    },
    polylinePath() {
      const path = [];
      if (this.startStop && this.startStop.lat) {
        path.push({
          lat: Number(this.startStop.lat),
          lng: Number(this.startStop.lng),
        });
      }
      this.mappableStops.forEach((stop) => {
        path.push({ lat: Number(stop.lat), lng: Number(stop.lng) });
      });
      return path;
    },
    // Road-following line once Directions resolved it for the CURRENT stops;
    // straight segments meanwhile (and whenever Directions is unavailable).
    displayPolylinePath() {
      return this.roadPath &&
        this.roadPathKey === JSON.stringify(this.polylinePath)
        ? this.roadPath
        : this.polylinePath;
    },
  },
  watch: {
    // Debounced: stop edits fire in bursts while building, and every
    // Directions request is billed — only ask once the route settles.
    polylinePath(path) {
      clearTimeout(this.roadPathTimer);
      if (path.length < 2) return;
      this.roadPathTimer = setTimeout(() => {
        const key = JSON.stringify(this.polylinePath);
        if (key === this.roadPathKey && this.roadPath) return;
        this.roadPathKey = key;
        fleetRoadPath(this.polylinePath)
          .then((road) => {
            if (this.roadPathKey === key) this.roadPath = road;
          })
          .catch(() => {});
      }, 800);
    },
  },
  methods: {
    BackToList() {
      this.$router.push({ name: "fleet.routes.index" });
    },
    CycleMapMode() {
      const index = this.mapModes.findIndex(
        (mode) => mode.id === this.mapTypeId
      );
      this.mapTypeId = this.mapModes[(index + 1) % this.mapModes.length].id;
    },
    ArmPlacement(target) {
      this.placementTarget =
        target === "start"
          ? { kind: "start" }
          : { kind: "stop", index: target };
      const stop = this.TargetStop();
      this.coordText =
        stop && Number(stop.lat) ? `${stop.lat}, ${stop.lng}` : "";
      if (stop && Number(stop.lat)) {
        this.mapCenter = { lat: Number(stop.lat), lng: Number(stop.lng) };
      }
    },
    TargetStop() {
      if (!this.placementTarget) return null;
      if (this.placementTarget.kind === "start") return this.startStop;
      return this.routeStops[this.placementTarget.index] || null;
    },
    // Apply coords to the armed target (from map click, drag, or typing).
    SetTargetCoords(lat, lng) {
      if (!this.placementTarget) return;
      if (this.placementTarget.kind === "start") {
        this.startStop = { ...this.startStop, lat, lng, coordsDirty: true };
      } else {
        const index = this.placementTarget.index;
        this.$set(this.routeStops, index, {
          ...this.routeStops[index],
          lat,
          lng,
          coordsDirty: true,
        });
      }
    },
    OnMapClick(event) {
      if (!this.placementTarget || !event.latLng) return;
      const lat = Number(event.latLng.lat().toFixed(7));
      const lng = Number(event.latLng.lng().toFixed(7));
      this.SetTargetCoords(lat, lng);
      this.coordText = `${lat}, ${lng}`;
      this.mapCenter = { lat, lng };
      // Stays armed: keep clicking / dragging / typing until ບັນທຶກ or ປິດ.
    },
    OnStartDragEnd(event) {
      const lat = Number(event.latLng.lat().toFixed(7));
      const lng = Number(event.latLng.lng().toFixed(7));
      this.startStop = { ...this.startStop, lat, lng, coordsDirty: true };
      if (this.placementTarget && this.placementTarget.kind === "start") {
        this.coordText = `${lat}, ${lng}`;
      }
    },
    OnStopDragEnd(index, event) {
      const lat = Number(event.latLng.lat().toFixed(7));
      const lng = Number(event.latLng.lng().toFixed(7));
      this.$set(this.routeStops, index, {
        ...this.routeStops[index],
        lat,
        lng,
        coordsDirty: true,
      });
      if (
        this.placementTarget &&
        this.placementTarget.kind === "stop" &&
        this.placementTarget.index === index
      ) {
        this.coordText = `${lat}, ${lng}`;
      }
    },
    // Manual entry: live-sync the pin while typing/pasting into the ONE box.
    ApplyCoordInput() {
      const parsed = this.ParseCoordText();
      if (parsed) {
        this.SetTargetCoords(parsed.lat, parsed.lng);
      }
    },
    /**
     * Tolerant "lat, lng" parser for the single coordinate box. Accepts what
     * people actually paste from Google Maps: the right-click coordinate copy
     * ("19.884537, 102.135087"), the same wrapped in parentheses, a
     * space-separated pair, or a whole Maps URL (…/@19.88,102.13,17z or
     * ?q=19.88,102.13). Returns {lat, lng} or null while incomplete/invalid.
     */
    ParseCoordText() {
      let text = String(this.coordText || "").trim();
      if (!text) return null;
      const urlPair = text.match(/[@=](-?\d{1,3}\.\d+)\s*,\s*(-?\d{1,3}\.\d+)/);
      if (urlPair) {
        text = `${urlPair[1]},${urlPair[2]}`;
      }
      const parts = text
        .replace(/[()]/g, " ")
        .split(/[,\s]+/)
        .filter(Boolean);
      if (parts.length < 2) return null;
      const lat = parseFloat(parts[0]);
      const lng = parseFloat(parts[1]);
      return this.ValidCoords(lat, lng) ? { lat, lng } : null;
    },
    ValidCoords(lat, lng) {
      return (
        Number.isFinite(lat) &&
        Number.isFinite(lng) &&
        lat !== 0 &&
        lng !== 0 &&
        lat >= -90 &&
        lat <= 90 &&
        lng >= -180 &&
        lng <= 180
      );
    },
    CenterOnTarget() {
      const stop = this.TargetStop();
      if (stop && Number(stop.lat)) {
        this.mapCenter = { lat: Number(stop.lat), lng: Number(stop.lng) };
      }
    },
    SaveCoordEditor() {
      const parsed = this.ParseCoordText();
      if (!parsed) {
        this.$notification.error({
          message:
            'ພິກັດບໍ່ຖືກຕ້ອງ — ວາງແບບ "19.8845, 102.1350" ຈາກ Google Maps',
        });
        return;
      }
      const { lat, lng } = parsed;
      this.SetTargetCoords(lat, lng);
      const target = this.placementTarget;
      const stop = this.TargetStop();
      this.coordSaving = true;
      this.SaveStopPointLocation(stop.type, stop.id, lat, lng)
        .then(() => {
          this.coordSaving = false;
          if (target.kind === "start") {
            this.startStop = { ...this.startStop, coordsDirty: false };
          } else {
            this.$set(this.routeStops, target.index, {
              ...this.routeStops[target.index],
              coordsDirty: false,
            });
          }
          this.mapCenter = { lat, lng };
          if (this.placementTarget === target) {
            this.placementTarget = null;
          }
        })
        .catch(() => {
          this.coordSaving = false;
        });
    },
    SaveStartLocation() {
      this.SaveStopPointLocation(
        this.startStop.type,
        this.startStop.id,
        this.startStop.lat,
        this.startStop.lng
      )
        .then(() => {
          this.startStop = { ...this.startStop, coordsDirty: false };
        })
        .catch(() => {});
    },
    SaveStopLocation(index) {
      const stop = this.routeStops[index];
      this.SaveStopPointLocation(stop.type, stop.id, stop.lat, stop.lng)
        .then(() => {
          this.$set(this.routeStops, index, { ...stop, coordsDirty: false });
        })
        .catch(() => {});
    },
    SetStartStop(point) {
      this.startStop = {
        type: point.type,
        id: point.id,
        name: point.name,
        lat: coord(point.lat),
        lng: coord(point.lng),
        coordsDirty: false,
      };
      if (this.startStop.lat) {
        this.mapCenter = { lat: this.startStop.lat, lng: this.startStop.lng };
      } else {
        this.$notification.warning({
          message: "ຈຸດນີ້ຍັງບໍ່ມີພິກັດ — ກົດໃສ່ແຜນທີ່ເພື່ອວາງໝຸດ",
        });
        this.ArmPlacement("start");
      }
    },
    AddStop(point) {
      const exists = this.routeStops.some(
        (stop) => stop.type === point.type && stop.id === point.id
      );
      if (exists) {
        this.$notification.warning({ message: "ຈຸດແວະນີ້ຢູ່ໃນເສັ້ນທາງແລ້ວ" });
        return;
      }
      this.routeStops.push({
        type: point.type,
        id: point.id,
        name: point.name,
        lat: coord(point.lat),
        lng: coord(point.lng),
        stop_role: "both",
        planned_offset_min: "",
        coordsDirty: false,
      });
      const added = this.routeStops[this.routeStops.length - 1];
      if (added.lat) {
        this.mapCenter = { lat: added.lat, lng: added.lng };
      } else {
        this.$notification.warning({
          message: "ຈຸດນີ້ຍັງບໍ່ມີພິກັດ — ກົດໃສ່ແຜນທີ່ເພື່ອວາງໝຸດ",
        });
        this.ArmPlacement(this.routeStops.length - 1);
      }
    },
    RemoveStop(index) {
      this.placementTarget = null; // armed index would go stale
      this.routeStops.splice(index, 1);
    },
    // Clear the current start so a new one can be picked from search
    // ("ຈຸດເລີ່ມ"). The backend swaps start_stop_type/id on the next save.
    ClearStartStop() {
      if (this.placementTarget && this.placementTarget.kind === "start") {
        this.placementTarget = null;
      }
      this.startStop = null;
    },
    MoveStopUp(index) {
      if (index <= 0) return;
      this.placementTarget = null;
      const stops = [...this.routeStops];
      [stops[index - 1], stops[index]] = [stops[index], stops[index - 1]];
      this.routeStops = stops;
    },
    MoveStopDown(index) {
      if (index >= this.routeStops.length - 1) return;
      this.placementTarget = null;
      const stops = [...this.routeStops];
      [stops[index + 1], stops[index]] = [stops[index], stops[index + 1]];
      this.routeStops = stops;
    },
    ValidateAndSave() {
      this.$validator.validateAll().then((result) => {
        if (!result) return;
        if (!this.startStop) {
          this.$notification.error({ message: "ກະລຸນາເລືອກຈຸດເລີ່ມຕົ້ນກ່ອນ" });
          return;
        }
        if (!this.routeStops.length) {
          this.$notification.error({
            message: "ກະລຸນາເພີ່ມຈຸດແວະຢ່າງໜ້ອຍ 1 ຈຸດ",
          });
          return;
        }
        const save = this.editingRouteId
          ? this.UpdateRoute(this.editingRouteId)
          : this.CreateRoute();
        save
          .then((route) => {
            this.routeDetail = route;
            if (!this.editingRouteId && route && route.id) {
              this.editingRouteId = route.id;
              this.$router
                .replace({
                  name: "fleet.routes.builder",
                  query: { id: String(route.id) },
                })
                .catch(() => {});
            }
          })
          .catch(() => {});
      });
    },
    ToggleActive() {
      // Surfaces ROUTE_HAS_NO_STOPS / ROUTE_HAS_NO_START from the status endpoint.
      this.SetRouteStatus(this.editingRouteId, !this.routeDetail.is_active)
        .then((route) => {
          this.routeDetail = route;
        })
        .catch(() => {});
    },
    LoadRouteForEdit(routeId) {
      this.FetchRouteDetail(routeId)
        .then((route) => {
          // Also drives the status chip + activate button, which previously only
          // appeared after a save because nothing set routeDetail on load.
          this.routeDetail = route;
          this.routeValue = {
            name: route.name,
            code: route.code || "",
            distance_km: route.distance_km,
            standard_trip_price: route.standard_trip_price,
            standard_fuel_liters: route.standard_fuel_liters,
            standard_fuel_amount: route.standard_fuel_amount,
            trips_per_week: route.trips_per_week,
            running_route: route.running_route || "",
            auto_daily_rounds: !!route.auto_daily_rounds,
          };
          this.startStop = route.start_stop_type
            ? {
                type: route.start_stop_type,
                id: route.start_stop_id,
                name:
                  (route.start_stop_facility &&
                    route.start_stop_facility.name) ||
                  `#${route.start_stop_id}`,
                lat: coord(
                  route.start_stop_facility && route.start_stop_facility.lat
                ),
                lng: coord(
                  route.start_stop_facility && route.start_stop_facility.lng
                ),
                coordsDirty: false,
              }
            : null;
          this.routeStops = (route.stops || []).map((stop) => ({
            type: stop.stop_type,
            id: stop.stop_id,
            name:
              (stop.facility && stop.facility.name) ||
              `${stop.stop_type} #${stop.stop_id}`,
            lat: coord(stop.facility && stop.facility.lat),
            lng: coord(stop.facility && stop.facility.lng),
            stop_role: stop.stop_role || "both",
            planned_offset_min:
              stop.planned_offset_min === null ? "" : stop.planned_offset_min,
            coordsDirty: false,
          }));
          if (this.startStop && this.startStop.lat) {
            this.mapCenter = {
              lat: this.startStop.lat,
              lng: this.startStop.lng,
            };
          }
        })
        .catch(() => {
          this.$notification.error({ message: "ບໍ່ພົບຂໍ້ມູນເສັ້ນທາງ" });
        });
    },
  },
  created() {
    this.FetchProvinces();
    const routeId = this.$route.query.id;
    if (routeId) {
      this.editingRouteId = Number(routeId);
      this.LoadRouteForEdit(this.editingRouteId);
    }
  },
  beforeDestroy() {
    clearTimeout(this.roadPathTimer);
  },
};
</script>

<style lang="scss" scoped>
.route-name-top {
  margin: 4px 0 10px;
  padding: 8px 12px;
  background: #f4f6ff;
  border-left: 3px solid #195bff;
  border-radius: 3px;
  font-size: 14px;
  color: #333;
  word-break: break-word; // long province route strings must wrap, never clip

  i {
    margin-right: 6px;
    color: #195bff;
  }
}

.map-wrap {
  position: relative;

  .map-mode-btn {
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 5;
    background: white;
    border: none;
    border-radius: 3px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    padding: 8px 14px;
    font-size: 13px;
    font-family: inherit;
    cursor: pointer;

    i {
      margin-right: 6px;
      color: #195bff;
    }

    &:hover {
      background: #f4f6ff;
    }
  }

  .map-placement-banner {
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 5;
    background: #e8a10f;
    color: white;
    padding: 6px 16px;
    border-radius: 20px;
    font-size: 13px;
    white-space: nowrap;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);

    a {
      color: white;
      text-decoration: underline;
      margin-left: 10px;
      cursor: pointer;
    }
  }
}

.coord-editor {
  margin-top: 10px;

  .coord-actions {
    display: flex;
    align-items: center;
    gap: 5px;

    .vs-button {
      margin: 0;
    }
  }
}

.stop-search-results {
  margin-top: 10px;
  max-height: 220px;
  overflow-y: auto;
  border-top: 1px solid #eee;
}

/* Geofence proximity advisory (001-fleet-gps-tracking) */
.geofence-warn {
  margin-top: 12px;
  padding: 10px 12px;
  border-radius: 6px;
  background: #fff8e6;
  border-left: 4px solid #e8a10f;
  text-align: left;

  .geofence-warn-title {
    color: #e8a10f;
    font-weight: bold;
    font-size: 13px;
    margin-bottom: 4px;
  }

  .geofence-warn-line {
    font-size: 13px;
    line-height: 1.6;
  }

  .geofence-warn-note {
    font-size: 11px;
    color: grey;
    margin-top: 6px;
    line-height: 1.5;
  }
}

.stop-search-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px solid #f4f4f4;
  font-size: 13px;
}

.stop-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  border-bottom: 1px solid #f4f4f4;
  font-size: 13px;

  &.start {
    font-weight: bold;
  }

  .stop-seq {
    width: 26px;
    height: 26px;
    min-width: 26px;
    border-radius: 50%;
    background: #195bff;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    margin-right: 8px;
  }

  .stop-name {
    flex: 1;
  }

  .stop-offset {
    width: 80px;
    margin-right: 5px;

    .input {
      height: 28px;
      font-size: 12px;
    }
  }

  .stop-actions {
    display: flex;
    align-items: center;
  }
}
</style>
